import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { buildRelatedPosts, servesOwnUrl, type RelatedPost } from "@/lib/related-posts";

import { BLOG_PAGE_SIZE as PAGE_SIZE, REDIRECTED_POST_SLUGS } from "@/lib/blog-listing";

export const listBlogPosts = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z
      .object({
        page: z.number().int().min(1).max(500).default(1),
        categorySlug: z.string().min(1).max(200).optional(),
        tagSlug: z.string().min(1).max(200).optional(),
        authorSlug: z.string().min(1).max(200).optional(),
        q: z.string().min(1).max(100).optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const { supabasePublic } = await import("@/lib/supabase-public.server");

    let postIds: string[] | null = null;
    if (data.categorySlug || data.tagSlug || data.authorSlug) {
      if (data.authorSlug) {
        const { data: author } = await supabasePublic
          .from("blog_authors").select("id").eq("slug", data.authorSlug).maybeSingle();
        if (!author) return { posts: [], total: 0, page: data.page, pageSize: PAGE_SIZE };
        const { data: rows } = await supabasePublic
          .from("blog_posts").select("id").eq("author_id", author.id).eq("status", "published");
        postIds = (rows ?? []).map((r) => r.id);
      } else if (data.categorySlug) {
        const { data: cat } = await supabasePublic
          .from("blog_categories").select("id").eq("slug", data.categorySlug).maybeSingle();
        if (!cat) return { posts: [], total: 0, page: data.page, pageSize: PAGE_SIZE };
        const { data: links } = await supabasePublic
          .from("blog_post_terms").select("post_id").eq("category_id", cat.id);
        postIds = (links ?? []).map((l) => l.post_id);
      } else if (data.tagSlug) {
        const { data: tag } = await supabasePublic
          .from("blog_tags").select("id").eq("slug", data.tagSlug).maybeSingle();
        if (!tag) return { posts: [], total: 0, page: data.page, pageSize: PAGE_SIZE };
        const { data: links } = await supabasePublic
          .from("blog_post_terms").select("post_id").eq("tag_id", tag.id);
        postIds = (links ?? []).map((l) => l.post_id);
      }
      if (!postIds || postIds.length === 0) {
        return { posts: [], total: 0, page: data.page, pageSize: PAGE_SIZE };
      }
    }

    const from = (data.page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    let q = supabasePublic
      .from("blog_posts")
      .select("id, slug, title, excerpt, featured_image_url, published_at, author:blog_authors(name, slug)", { count: "exact" })
      .eq("status", "published")
      .not("slug", "in", `(${REDIRECTED_POST_SLUGS.join(",")})`)
      .order("published_at", { ascending: false })
      .range(from, to);
    if (postIds) q = q.in("id", postIds);
    if (data.q) q = q.ilike("title", `%${data.q}%`);
    const { data: posts, count, error } = await q;
    if (error?.code === "PGRST103") {
      // A page number past the last page. PostgREST answers 416 "Requested
      // range not satisfiable", and this threw, so /category/hair-loss/?page=99
      // returned a 500. There are no posts on that page: say so.
      return { posts: [], total: 0, page: data.page, pageSize: PAGE_SIZE };
    }
    if (error) {
      console.error("[listBlogPosts]", error);
      throw new Error("Failed to load posts.");
    }
    // A post served from its wp-parity file shows that file's title on its
    // card, the title on its page, not the Supabase title.
    const { codeServedCard } = await import("@/lib/wp-parity.server");
    return { posts: (posts ?? []).map(codeServedCard), total: count ?? 0, page: data.page, pageSize: PAGE_SIZE };
  });

/** Categories that have at least one published post (excluding retired ones). */
export const listBlogCategories = createServerFn({ method: "GET" }).handler(async () => {
  const { supabasePublic } = await import("@/lib/supabase-public.server");
  const { data: cats, error } = await supabasePublic
    .from("blog_categories")
    .select("id, name, slug, blog_post_terms(post_id)")
    .order("name", { ascending: true });
  if (error) {
    console.error("[listBlogCategories]", error);
    return [] as { name: string; slug: string }[];
  }
  const HIDDEN = new Set(["js-results"]);
  return (cats ?? [])
    .filter((c) => !HIDDEN.has(c.slug) && (c.blog_post_terms?.length ?? 0) > 0)
    .map((c) => ({ name: c.name, slug: c.slug }));
});

// Some legacy posts were imported with Cyrillic lookalike letters in their
// slugs (e.g. "lаѕеrcap-trеаtmеnt"). Those slugs have been cleaned to Latin,
// but old links/bookmarks still arrive with the Cyrillic characters. Map the
// lookalikes back to Latin so those URLs resolve to the cleaned post.
const CYRILLIC_HOMOGLYPHS: Record<string, string> = {
  "а": "a", "е": "e", "і": "i", "о": "o", "р": "p", "ѕ": "s",
  "у": "y", "х": "x", "с": "c", "ј": "j", "һ": "h",
  "А": "A", "Е": "E", "І": "I", "О": "O", "Р": "P", "Ѕ": "S",
  "У": "Y", "Х": "X", "С": "C", "Ј": "J", "Һ": "H",
};

export function latinizeHomoglyphs(value: string): string {
  return value.replace(/[аеіорѕухсјһАЕІОРЅУХСЈҺ]/g, (ch) => CYRILLIC_HOMOGLYPHS[ch] ?? ch);
}

// No canonical_url: the page builds its own canonical, and the stored value
// for the LaserCap post is its old Cyrillic-lookalike URL, which redirects.
const POST_SELECT =
  "id, slug, title, excerpt, content_html, featured_image_url, published_at, updated_at, meta_title, meta_description, og_image_url, author:blog_authors(id, name, slug, bio, avatar_url)";

export const getBlogPost = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ slug: z.string().min(1).max(300) }).parse(d),
  )
  .handler(async ({ data }) => {
    const { mark } = await import("@/lib/request-trace");
    mark("getBlogPost");
    const { supabasePublic } = await import("@/lib/supabase-public.server");

    let { data: post, error } = await supabasePublic
      .from("blog_posts")
      .select(POST_SELECT)
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();

    if (!post && !error) {
      // Retry with the homoglyph-normalized slug (legacy Cyrillic URLs).
      const latin = latinizeHomoglyphs(data.slug);
      if (latin !== data.slug) {
        ({ data: post, error } = await supabasePublic
          .from("blog_posts")
          .select(POST_SELECT)
          .eq("slug", latin)
          .eq("status", "published")
          .maybeSingle());
      }
    }

    if (error) {
      console.error("[getBlogPost]", error);
      throw new Error("Failed to load post.");
    }
    mark("getBlogPost.post");
    // A post kept in this repo, with no Supabase row (src/lib/local-posts.ts).
    // Its title and body come from its wp-parity file below.
    let local = false;
    if (!post) {
      const { findLocalPost } = await import("@/lib/local-posts");
      const found = findLocalPost(data.slug);
      if (!found) return null;
      local = true;
      post = {
        id: `local:${found.slug}`,
        slug: found.slug,
        title: "",
        excerpt: found.excerpt,
        content_html: "",
        featured_image_url: null,
        published_at: found.published_at,
        updated_at: found.updated_at,
        meta_title: null,
        meta_description: null,
        og_image_url: null,
        author: null,
      };
    }
    // WordPress copy for posts that lost rank after the 21 to 23 Sep 2026 move.
    const { applyWpParity } = await import("@/lib/wp-parity.server");
    // Clearer title and description, and pinned related links, for posts
    // Google found but did not index (src/lib/post-seo.ts).
    const { applyPostMeta, withPinnedRelated } = await import("@/lib/post-seo");
    // Point internal links at their final URLs here, on the server. The
    // post HTML also travels in the page's hydration data, and Google reads
    // URLs from there: rewriting only at render left about 60 old WordPress
    // paths (clone-of-*, /contact/, /blog/...) in every post's page source,
    // and Search Console listed them under "Page with redirect" (3 Nov 2026).
    const { rewriteInternalLinks } = await import("@/lib/blog-internal-links");
    const parity = applyPostMeta(applyWpParity(post));
    const served = { ...parity, content_html: rewriteInternalLinks(parity.content_html) };

    const [{ data: termLinks }, related] = local
      ? [{ data: [] as { category: { name: string; slug: string } | null; tag: { name: string; slug: string } | null }[] }, []]
      : await Promise.all([
          supabasePublic
            .from("blog_post_terms")
            .select("category:blog_categories(name, slug), tag:blog_tags(name, slug)")
            .eq("post_id", post.id),
          loadRelatedPosts(post.id),
        ]);

    mark("getBlogPost.terms");
    const categories = (termLinks ?? [])
      .map((t) => t.category)
      .filter((c): c is { name: string; slug: string } => c != null);
    const tags = (termLinks ?? [])
      .map((t) => t.tag)
      .filter((t): t is { name: string; slug: string } => t != null);
    return { ...served, categories, tags, related: withPinnedRelated(post.slug, related) };
  });

// PostgREST returns at most 1000 rows per request. Read in pages.
const READ_PAGE = 1000;

async function readAll<T>(read: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += READ_PAGE) {
    const { data, error } = await read(from, from + READ_PAGE - 1);
    if (error) throw error;
    rows.push(...(data ?? []));
    if ((data?.length ?? 0) < READ_PAGE) return rows;
  }
}

/**
 * The "Related articles" links for one post. A failed read gives an empty
 * list, so the post itself still renders.
 */
async function loadRelatedPosts(postId: string): Promise<RelatedPost[]> {
  const { supabasePublic } = await import("@/lib/supabase-public.server");
  try {
    const [posts, terms] = await Promise.all([
      readAll((from, to) =>
        supabasePublic
          .from("blog_posts")
          .select("id, slug, title, published_at")
          .eq("status", "published")
          .order("id", { ascending: true })
          .range(from, to),
      ),
      readAll((from, to) =>
        supabasePublic
          .from("blog_post_terms")
          .select("post_id, category_id, tag_id")
          .order("id", { ascending: true })
          .range(from, to),
      ),
    ]);
    const byPost = new Map<string, { categoryIds: string[]; tagIds: string[] }>();
    for (const t of terms) {
      const entry = byPost.get(t.post_id) ?? { categoryIds: [], tagIds: [] };
      if (t.category_id) entry.categoryIds.push(t.category_id);
      if (t.tag_id) entry.tagIds.push(t.tag_id);
      byPost.set(t.post_id, entry);
    }
    const input = posts
      .filter((p) => servesOwnUrl(p.slug, REDIRECTED_POST_SLUGS))
      .map((p) => ({ ...p, ...(byPost.get(p.id) ?? { categoryIds: [], tagIds: [] }) }));
    // Picked on the Supabase titles, then shown with the title on each
    // post's page (wp-parity files), so the picks themselves do not change.
    const { codeServedCard } = await import("@/lib/wp-parity.server");
    return (buildRelatedPosts(input).get(postId) ?? []).map(codeServedCard);
  } catch (err) {
    console.error("[loadRelatedPosts]", err);
    return [];
  }
}

export type BlogTaxonomyMeta = {
  name: string;
  slug: string;
  description?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
} | null;

export const listBlogTaxonomyMeta = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({
      kind: z.enum(["category", "tag", "author"]),
      slug: z.string().min(1).max(200),
    }).parse(d),
  )
  .handler(async ({ data }): Promise<BlogTaxonomyMeta> => {
    const { supabasePublic } = await import("@/lib/supabase-public.server");
    if (data.kind === "category") {
      const { data: row } = await supabasePublic
        .from("blog_categories").select("name, slug, description").eq("slug", data.slug).maybeSingle();
      return row ?? null;
    }
    if (data.kind === "tag") {
      const { data: row } = await supabasePublic
        .from("blog_tags").select("name, slug").eq("slug", data.slug).maybeSingle();
      return row ?? null;
    }
    const { data: row } = await supabasePublic
      .from("blog_authors").select("name, slug, bio, avatar_url").eq("slug", data.slug).maybeSingle();
    return row ?? null;
  });